import React, { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { Plus } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../ModalContainer/ModelContainer'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import Tabs from '../../../TabsV2/Tabs'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import {
  selectVoicemailMessages,
  deleteVoicemailMessage,
  createVoicemailMessage,
  updateVoicemailMessageAction,
} from '../../../../reducers/chatReducer'

import VoicemailItem from './components/VoicemailItem/VoicemailItem'
import RecordTab from './tabs/RecordTab/RecordTab'
import UploadTab from './tabs/UploadTab/UploadTab'
import TextToSpeechTab from './tabs/TextToSpeechTab/TextToSpeechTab'
import { voicemailFormValidation } from './../helpers'

const ManageVoicemailWindow = ({
  onClose = () => {},
  inboxId,
  voicemailMessages,
  deleteVoicemailMessage,
  createVoicemailMessage,
  updateVoicemailMessageAction,
}) => {
  const [showAddNewGreeting, setShowAddNewGreeting] = useState(false)
  const [deleteVoicemail, setDeleteVoicemail] = useState(null)
  const [editVoicemail, setEditVoicemail] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { displaySnackbar } = useSnackbar()
  const { t } = useTranslation(['chat'])

  const validationSchema = Yup.object().shape(voicemailFormValidation(t))

  const onCancel = () => {
    if (showAddNewGreeting) {
      setShowAddNewGreeting(false)
      setEditVoicemail(null)
    } else {
      onClose()
    }
  }

  const onEdit = (voicemail) => {
    setEditVoicemail(voicemail)
    setShowAddNewGreeting(true)
  }

  const removeVoicemail = async () => {
    try {
      setIsLoading(true)
      await deleteVoicemailMessage(inboxId, deleteVoicemail.id)
      displaySnackbar(t('chat:manageVoicemail.deleteSuccess'))
    } catch ({ message }) {
      displaySnackbar(t('chat:manageVoicemail.deleteError', { message }), true)
    } finally {
      setIsLoading(false)
      setDeleteVoicemail(null)
    }
  }

  const onSubmit = async ({ name, text, recording }, { setSubmitting }) => {
    try {
      setSubmitting(true)
      const data = {
        name,
        inboxId,
      }
      if (recording) {
        const recordingFile = new File([recording], `${name}.mp3`, { type: 'audio/mpeg' })
        data.recording = recordingFile
        data.type = 'recording'
        data.encode = true
      } else {
        data.text = text
        data.type = 'text'
      }

      if (editVoicemail) {
        await updateVoicemailMessageAction(inboxId, editVoicemail.id, data)
      } else {
        await createVoicemailMessage(inboxId, data)
      }
      displaySnackbar(
        t('chat:manageVoicemail.saveSuccess', {
          action: editVoicemail
            ? t('chat:manageVoicemail.actionUpdated')
            : t('chat:manageVoicemail.actionCreated'),
        }),
      )
    } catch ({ message }) {
      displaySnackbar(
        t('chat:manageVoicemail.saveError', {
          action: editVoicemail
            ? t('chat:manageVoicemail.actionUpdate')
            : t('chat:manageVoicemail.actionCreate'),
          message,
        }),
        true,
      )
    } finally {
      setSubmitting(false)
      setShowAddNewGreeting(false)
      setEditVoicemail(null)
    }
  }

  return (
    <React.Fragment>
      <ModalContainer
        additionalClass={`manage-voicemail-window ${isLoading ? 'is-loading' : ''}`}
        onClose={onClose}
        headerTitle={t('chat:manageVoicemail.title')}
      >
        {showAddNewGreeting ? (
          <Formik
            initialValues={
              editVoicemail
                ? { name: editVoicemail.name, text: editVoicemail.text }
                : { name: '', text: '', recording: null }
            }
            onSubmit={onSubmit}
            validationSchema={validationSchema}
          >
            {({
              handleSubmit,
              handleChange,
              setFieldTouched,
              setFieldValue,
              resetForm,
              values,
              errors,
              touched,
              isSubmitting,
              isValid,
              dirty,
            }) => (
              <React.Fragment>
                <form
                  className="manage-voicemail-window-container"
                  id="voicemail-form"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <TextField
                      name="name"
                      placeholder={t('chat:manageVoicemail.greetingNamePlaceholder')}
                      label={t('chat:manageVoicemail.greetingNameLabel')}
                      value={values.name}
                      isError={touched.name && !!errors.name}
                      errorLabel={touched.name && errors.name}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('name')}
                      maxLength={30}
                    />
                    <span className="input-desc-label">
                      {t('chat:manageVoicemail.greetingTextTip')}
                    </span>
                  </div>
                  <Tabs
                    onChange={() => resetForm({ name: values.name })}
                    disabled={!!editVoicemail}
                    tabs={[
                      {
                        heading: t('chat:manageVoicemail.tabRecord'),
                        content: (
                          <RecordTab
                            onChange={(file) => setFieldValue('recording', file)}
                            value={values.recording}
                          />
                        ),
                      },
                      {
                        heading: t('chat:manageVoicemail.tabUploadFile'),
                        content: (
                          <UploadTab
                            onChange={(file) => setFieldValue('recording', file)}
                            value={values.recording}
                          />
                        ),
                      },
                      {
                        heading: t('chat:manageVoicemail.tabTextToSpeech'),
                        content: <TextToSpeechTab onChange={handleChange} value={values.text} />,
                        default: !!editVoicemail,
                      },
                    ]}
                  />
                </form>
                <div className="actions-container">
                  <button
                    className="button button-gray"
                    type="button"
                    onClick={() => {
                      resetForm()
                      onCancel()
                    }}
                  >
                    {t('common:actions.cancel')}
                  </button>
                  <button
                    className={`button button-primary ${isSubmitting ? 'is-loading' : ''}`}
                    type="submit"
                    disabled={
                      isSubmitting || !dirty || !isValid || (!values.recording && !values.text)
                    }
                    form="voicemail-form"
                  >
                    {t('common:actions.save')}
                  </button>
                </div>
              </React.Fragment>
            )}
          </Formik>
        ) : (
          <React.Fragment>
            <div className="manage-voicemail-window-container">
              <div className="voicemail-list">
                {voicemailMessages.map((voicemail) => (
                  <VoicemailItem
                    voicemail={voicemail}
                    key={voicemail.name}
                    onDelete={setDeleteVoicemail}
                    onEdit={onEdit}
                  />
                ))}
              </div>
              <button className="button button-primary" onClick={() => setShowAddNewGreeting(true)}>
                <Plus /> {t('chat:manageVoicemail.addNewGreeting')}
              </button>
            </div>
            <div className="actions-container">
              <button className="button button-gray" onClick={onCancel}>
                {t('common:actions.close')}
              </button>
            </div>
          </React.Fragment>
        )}
      </ModalContainer>
      {deleteVoicemail && (
        <ConfirmPopup
          cancelText={t('common:actions.cancel')}
          confirmText={t('common:actions.delete')}
          title={t('chat:manageVoicemail.deleteTitle')}
          body={t('chat:manageVoicemail.deleteBody', { name: deleteVoicemail.name })}
          cancelCallback={() => setDeleteVoicemail(null)}
          confirmCallback={!isLoading && removeVoicemail}
          isOpen={!!deleteVoicemail}
          isLoading={isLoading}
        />
      )}
    </React.Fragment>
  )
}

ManageVoicemailWindow.propTypes = {
  onClose: PropTypes.func,
  voicemailMessages: PropTypes.array,
  inboxId: PropTypes.number,
  deleteVoicemailMessage: PropTypes.func,
  createVoicemailMessage: PropTypes.func,
  updateVoicemailMessageAction: PropTypes.func,
}

export default connect(
  (store) => ({
    voicemailMessages: selectVoicemailMessages(store),
  }),
  {
    deleteVoicemailMessage,
    createVoicemailMessage,
    updateVoicemailMessageAction,
  },
)(ManageVoicemailWindow)
